/**
 * Shared contract for the "Check in" action on provider-facing session push
 * notifications (arrival push + session-start reminder). Used by:
 *
 *  - the backend senders (session-journey auto-arrival push, scan-due-reminders
 *    start reminder) — they stamp `data.notificationAction` and the APNs
 *    category;
 *  - the Android native renderer (SessionNotificationHelper.java — keep the
 *    string literals there in sync with these constants);
 *  - the iOS category registration (LocalNotifications.registerActionTypes
 *    registers UNNotificationCategory globally, so remote APNs alerts carrying
 *    `aps.category === SESSION_CHECK_IN_NOTIFICATION_CATEGORY` show the action);
 *  - the frontend push handler, which recognises either the Capacitor
 *    `actionId` (iOS category action) or `data.notificationAction` (Android
 *    native action intent) and performs the check-in immediately.
 *
 * HONEST BEHAVIOUR: the action cannot check in silently (check-in needs the
 * authenticated WebView + a GPS fix). Tapping it opens the app, which
 * IMMEDIATELY performs the check-in — no further taps — then shows the undo
 * snackbar on the session.
 */

/** APNs category id / iOS action-type id for check-in-capable session pushes. */
export const SESSION_CHECK_IN_NOTIFICATION_CATEGORY = 'session_checkin'

/** Action id inside the category / `data.notificationAction` value. */
export const SESSION_CHECK_IN_NOTIFICATION_ACTION = 'check-in'

/**
 * User-facing label for the action. Terminology (owner decision 2026-10-07):
 * the recorded moment is the "arrival time" — never "Check in" or "Start
 * session". Keep SessionNotificationHelper.java's literal in sync.
 */
export const SESSION_CHECK_IN_NOTIFICATION_ACTION_TITLE = 'Record arrival'

/**
 * Local "Arrival time recorded · UNDO" notification shown when the dwell
 * auto-record fires (the provider may not be looking at the phone). The UNDO
 * action mirrors the in-app snackbar; both call the same undo-check-in event.
 */
export const SESSION_ARRIVAL_UNDO_NOTIFICATION_CATEGORY = 'session_arrival_undo'
export const SESSION_ARRIVAL_UNDO_NOTIFICATION_ACTION = 'undo-arrival'

/**
 * Local "Departure recorded · UNDO" notification shown when a departure is
 * recorded (2026-10-07 field feedback: ending a session was too easy and
 * irreversible). The UNDO action mirrors the in-app snackbar; both submit the
 * same undo-check-out event.
 */
export const SESSION_DEPARTURE_UNDO_NOTIFICATION_CATEGORY = 'session_departure_undo'
export const SESSION_DEPARTURE_UNDO_NOTIFICATION_ACTION = 'undo-departure'

/**
 * Local "Looks like you've left — record your departure?" notification shown
 * when GPS fixes leave the arrival geofence while the session is still
 * checked in (2026-10-08 field test: a departure was forgotten and recorded
 * late). The action opens the app and records the departure at the moment
 * the provider actually left the geofence. Push-privacy: the body never
 * carries the member name or address.
 */
export const SESSION_GEOFENCE_EXIT_NOTIFICATION_CATEGORY = 'session_geofence_exit'
export const SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION = 'record-departure'
export const SESSION_GEOFENCE_EXIT_NOTIFICATION_ACTION_TITLE = 'Record departure'
export const SESSION_GEOFENCE_EXIT_NOTIFICATION_TITLE = "Looks like you've left"
export const SESSION_GEOFENCE_EXIT_NOTIFICATION_BODY =
  'Record your departure? Tap to record it at the time you left, or ignore if you are still at the session.'
